import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from './context';
import { Failure, Rich } from './bits';
import { useT } from '../messages';

/**
 * The wizard's **Enable concierge** step.
 *
 * The Concierge card that used to live here beside it is now the Connections
 * tab's `ConciergeCard` (ui/business/connections/, console redesign phase B5),
 * which runs the same action behind a confirm and says what it did in words.
 * This step stays as the action on its own, because inside the wizard the
 * surrounding steps are the explanation.
 *
 * **There is no key on it, anywhere.** The action hands the API key and the
 * signing secret over machine to machine; what the answer carries is the key's
 * prefix, which is not a secret (K3).
 */

/**
 * The wizard step that replaces **two** of the old ones for `aiconcierge`: the
 * key shown once in an `OnceBox`, and the webhook URL typed in by hand. Both
 * were a person carrying credentials between two consoles, which is exactly
 * what the provisioning API exists to end.
 *
 * Other integrations keep today's flow, and the wizard says so.
 */
export function ConciergeEnable({
  tenantId,
  onDone,
}: {
  tenantId: string;
  onDone: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [result, setResult] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const read = useRead(() => api.concierge(tenantId), [tenantId]);

  const configured = read.value?.configured ?? false;

  const enable = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.enableConcierge(tenantId);
      setResult(t('concierge.enabled', { prefix: answer.key_prefix }));
      setWarnings(answer.warnings);
    });
    if (ok) read.reload();
  };

  return (
    <>
      <p className="muted">
        <Rich text={t('concierge.wizard.note')} />
      </p>

      {read.value !== null && !configured ? (
        <div className="by-design" role="note">
          <h4>{t('concierge.notConfigured')}</h4>
          <p>
            {t('concierge.notConfiguredVars', {
              vars: (read.value.missing_config ?? []).join(', '),
            })}
          </p>
        </div>
      ) : null}

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {result !== null ? <p className="ok">{result}</p> : null}
      {warnings.map((w) => (
        <p key={w} className="warn">
          {w}
        </p>
      ))}

      <p className="muted">{t('concierge.wizard.other')}</p>

      <div className="row-actions">
        {configured ? (
          <button
            type="button"
            className="button button--primary"
            onClick={() => void enable()}
            disabled={action.busy}
          >
            {action.busy ? t('concierge.enabling') : t('concierge.enable')}
          </button>
        ) : null}
        <button type="button" className="button" onClick={onDone}>
          {t('common.continue')}
        </button>
      </div>
    </>
  );
}
